import type { ServiceItem, ServiceTransaction } from '../../types';
import { isHourlyUnit } from '../serviceUnits';
import { flatCoverageHints, isCovered } from '../flatRateLogic';
import { hatBestandteile } from '../bestandteile/bestandteilLogic';
import type { SifaTeilnahme } from './types';

/**
 * Die Buchung von UNTERWEISUNGS-Teilnahmen auf eine LV-Position — als pure
 * Funktion neben der Oberflaeche (Muster einweisungBooking.ts).
 *
 * WARUM PURE: kein DOM in den Tests, und ein Fehler in diesem Pfad kostet
 * Geld. Die Funktion liest und schreibt nichts; sie gibt die NEUE
 * Leistungsliste und den Log-Eintrag zurueck. Der Aufrufer (Innendienst-Modul)
 * geht in DIESER Reihenfolge vor:
 *   1. `sifa_teilnahme_gebucht_markieren` (sifaService.markiereGebucht) —
 *      der DB-Riegel; die Rueckgabe sagt, WIE VIELE Zeilen noch ungebucht
 *      waren. Weicht sie von `teilnahmeIds.length` ab, wird NICHT gebucht,
 *      sondern neu geladen und erneut gerechnet.
 *   2. Leistungsliste + Log-Eintrag ueber `onUpdate` ins Projekt.
 *   3. `queueJournalMirror({ projectId, entries: [logEntry], source, … })`.
 * Nie still, nur auf ausdrueckliche Handlung (Betreiber-Entscheidung 4).
 *
 * FACHLICH: Einheit Stueck, Menge = Anzahl Teilnahmen. Rechnet die Position
 * in STUNDEN ab, ist die Menge die Summe der Kursdauer je Teilnahme (Viertel-
 * stunden gerundet). Ist die Position durch eine MONATSPAUSCHALE gedeckt,
 * aendert das an der Buchung NICHTS: der Zaehler laeuft, der Geldstand steht
 * (Kernregel in flatRateLogic.ts) — die Anzeige sagt das ueber
 * `pauschalHinweis`, die Buchung selbst weiss nichts davon.
 */

export type ServiceLogEntry = ServiceTransaction;

/** Was der Log-Text ueber die gebuchte Unterweisung sagen soll. */
export interface UnterweisungBuchungsKontext {
  /** Anzeigename des Buchenden; ohne ihn steht „System" im Protokoll. */
  performerName?: string;
  /** Buchungstag, ISO `YYYY-MM-DD` — Teil des `bookingReference`. */
  datum: string;
  unterweisungTitel: string;
  /** Laufende Nummer der Unterweisung je Projekt (SifaUnterweisung.nummer). */
  unterweisungNummer: number;
  /**
   * Kursdauer in Minuten (SifaUnterweisung.dauerMin) — nur fuer eine
   * STUNDENposition von Belang. Fehlt sie, zaehlt die tatsaechliche Dauer
   * aus `begonnenAm`/`abgeschlossenAm` je Teilnahme.
   */
  dauerMin?: number | null;
}

export interface UnterweisungBuchung {
  /** Die vollstaendige Leistungsliste MIT der gebuchten Position. */
  services: ServiceItem[];
  /** Der Eintrag, der an `globalData.serviceLog` angehaengt wird. */
  logEntry: ServiceLogEntry;
  /** Gebuchte Menge (Stueck oder Stunden) — dieselbe Zahl wie logEntry.quantity. */
  menge: number;
  /** Die Teilnahmen, die in diese Buchung eingegangen sind — fuer markiereGebucht. */
  teilnahmeIds: string[];
  /** Position rechnet in Stunden ab. */
  stundenposition: boolean;
}

// ─── Positionen ─────────────────────────────────────────────────────────────

/**
 * Welche LV-Positionen der Abrechnungsdialog anbietet.
 *
 * Dieselbe zweistufige Regel wie selectBillableServices (billingSelection.ts):
 * zuerst die ZUORDNUNG `unterweisung` (fachliche Wahrheit), daneben
 * unklassifizierte Positionen mit „Unterweisung" im Titel. Eine ausdrueckliche
 * fremde Zuordnung sperrt den Textarm — „Einweisung" (SiGe-Plan) ist etwas
 * anderes und faengt sich hier auch nicht: das Stichwort ist bewusst das
 * ganze Wort „unterweisung". Kein Rueckfall auf alle Positionen: lieber die
 * Zeile „ohne Leistungsbuchung" als das Falsche.
 *
 * Eigene Fassung statt Erweiterung von BillingModule, weil der Wert
 * `unterweisung` dort die Keyword-Tabelle und deren Tests mitziehen wuerde —
 * die Unterweisung braucht sie nicht.
 *
 * Nie eine PAKETPOSITION (Positionen mit Bestandteilen, Plan Kernregel 7) —
 * auch nicht mit Zuordnung `unterweisung`: ein Paket bucht nur der
 * Abschlussschritt bzw. der Dialog der Bestandteile, wenn alles erledigt ist.
 */
export const waehleUnterweisungsPositionen = (services: ServiceItem[] | null | undefined): ServiceItem[] =>
  (services ?? []).filter((s) => {
    if (!s || hatBestandteile(s)) return false;
    if (s.assignment === 'unterweisung') return true;
    if (s.assignment !== undefined && s.assignment !== 'none') return false;
    return (s.title || '').toLowerCase().includes('unterweisung');
  });

/** Ist die Position durch eine Monatspauschale gedeckt? (Anzeige — die Buchung aendert das nicht.) */
export const istPauschalGedeckt = (services: ServiceItem[] | null | undefined, positionId: string): boolean =>
  isCovered(services, positionId);

/** Der Hinweistext „in der Monatspauschale enthalten (…)" oder null. Wortgleich mit den uebrigen Listen. */
export const pauschalHinweis = (services: ServiceItem[] | null | undefined, positionId: string): string | null =>
  flatCoverageHints(services).get(positionId) ?? null;

// ─── Teilnahmen ─────────────────────────────────────────────────────────────

/**
 * Teilnahmen, die gebucht werden duerfen: abgeschlossen und noch nicht
 * gebucht. `nurBestandene` laesst nicht bestandene weg — ob eine nicht
 * bestandene Durchfuehrung berechnet wird, entscheidet der Innendienst im
 * Dialog, nicht diese Funktion.
 */
export const buchbareTeilnahmen = (
  teilnahmen: ReadonlyArray<SifaTeilnahme> | null | undefined,
  options: { nurBestandene?: boolean } = {},
): SifaTeilnahme[] =>
  (teilnahmen ?? []).filter(
    (t) => !!t?.id && !!t.abgeschlossenAm && !t.gebuchtAm && (!options.nurBestandene || t.bestanden === true),
  );

/** Auf Viertelstunden gerundet (kaufmaennisch), nie unter 0. */
export const rundeViertelstunde = (stunden: number): number =>
  Number.isFinite(stunden) && stunden > 0 ? Math.round(stunden * 4) / 4 : 0;

/** Dauer einer Teilnahme in Minuten aus den Zeitstempeln; 0 bei Luecke oder Unsinn. */
const tatsaechlicheMinuten = (t: SifaTeilnahme): number => {
  const von = Date.parse(t.begonnenAm ?? '');
  const bis = Date.parse(t.abgeschlossenAm ?? '');
  if (!Number.isFinite(von) || !Number.isFinite(bis) || bis <= von) return 0;
  return (bis - von) / 60_000;
};

/**
 * Stundenmenge fuer eine Stundenposition: Kursdauer je Teilnahme, sonst die
 * tatsaechliche Dauer; Summe auf Viertelstunden gerundet, mindestens eine
 * Viertelstunde, sobald ueberhaupt eine Teilnahme vorliegt — eine Buchung mit
 * 0 im Protokoll sieht aus wie eine und ist keine.
 */
export const stundenmenge = (teilnahmen: ReadonlyArray<SifaTeilnahme>, dauerMin?: number | null): number => {
  if (teilnahmen.length === 0) return 0;
  const kurs = Number(dauerMin);
  const minuten = Number.isFinite(kurs) && kurs > 0
    ? kurs * teilnahmen.length
    : teilnahmen.reduce((sum, t) => sum + tatsaechlicheMinuten(t), 0);
  return Math.max(0.25, rundeViertelstunde(minuten / 60));
};

// ─── Buchung ────────────────────────────────────────────────────────────────

/** ISO-Datum (`YYYY-MM-DD`) in deutsche Schreibweise, sonst wortgetreu — per Zeichenkette, nie ueber new Date. */
const formatiereDatum = (iso?: string): string => {
  const text = (iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : text;
};

/** `YYYY-MM-DD` → `YYYYMMDD` fuer den bookingReference; sonst die Ziffern des Textes. */
const datumKompakt = (iso?: string): string => {
  const text = (iso || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
  return m ? `${m[1]}${m[2]}${m[3]}` : text.replace(/\D/g, '');
};

/**
 * Buchungskennung `UW-<nummer>-<yyyymmdd>`. Sie sagt, WELCHE Unterweisung an
 * WELCHEM Tag gebucht wurde; Journal und Leistungsnachweis erkennen sie am
 * Praefix. Kein `reportRef`: eine Unterweisung ist kein Begehungsbericht.
 */
export const unterweisungBookingReference = (nummer: number, datumIso: string): string =>
  `UW-${nummer}-${datumKompakt(datumIso)}`;

/**
 * Protokolltext: Titel und Nummer (der Bezug zum Kurs), Buchungstag, Anzahl
 * der Teilnahmen — dann wie im Bestand die gebuchte Menge mit Einheit.
 */
const buildDescription = (
  kontext: UnterweisungBuchungsKontext,
  anzahl: number,
  menge: number,
  unit: string,
): string => {
  const teile: string[] = [`Nr. ${kontext.unterweisungNummer}`];
  const datum = formatiereDatum(kontext.datum);
  if (datum) teile.push(datum);
  teile.push(anzahl === 1 ? '1 Teilnahme' : `${anzahl} Teilnahmen`);
  const titel = (kontext.unterweisungTitel || '').trim() || 'ohne Titel';
  return `Unterweisung „${titel}" (${teile.join(', ')}) (${menge} ${unit})`;
};

/**
 * Rechnet die Buchung der `teilnahmen` auf die Position `positionId`.
 *
 * `null`, wenn es die Position nicht gibt oder keine buchbare Teilnahme
 * uebrig bleibt — der Aufrufer darf dann NICHT schreiben. Bereits gebuchte
 * Teilnahmen (`gebuchtAm`) und Doppelte (gleiche id) werden hier NOCH EINMAL
 * ausgesiebt, obwohl der Dialog das schon tut: eine doppelt gezaehlte
 * Teilnahme ist Geld auf der Rechnung, und dieser Riegel kostet nichts.
 *
 * STATUS: `done`, sobald das Kontingent erreicht oder ueberschritten ist —
 * die Regel des Bestands. Ein Ueberschreiten wird nicht gedeckelt.
 */
export const rechneUnterweisungsBuchung = (
  services: ServiceItem[] | null | undefined,
  teilnahmen: ReadonlyArray<SifaTeilnahme> | null | undefined,
  positionId: string,
  kontext: UnterweisungBuchungsKontext,
): UnterweisungBuchung | null => {
  const liste = services ?? [];
  const index = liste.findIndex((s) => s.id === positionId);
  if (index === -1) return null;

  const gesehen = new Set<string>();
  const buchbar: SifaTeilnahme[] = [];
  for (const t of teilnahmen ?? []) {
    if (!t?.id || t.gebuchtAm || gesehen.has(t.id)) continue;
    gesehen.add(t.id);
    buchbar.push(t);
  }
  if (buchbar.length === 0) return null;

  const service = liste[index];
  const stundenposition = isHourlyUnit(service.unit);
  const menge = stundenposition ? stundenmenge(buchbar, kontext.dauerMin) : buchbar.length;
  const unit = stundenposition ? (service.unit || 'Stunden') : (service.unit || 'Stück');

  const newUsed = (service.quantityUsed || 0) + menge;
  const isDone = service.quantityContracted > 0 && newUsed >= service.quantityContracted;

  const newServices = [...liste];
  newServices[index] = {
    ...service,
    quantityUsed: newUsed,
    status: isDone ? 'done' : service.status,
  };

  const logEntry: ServiceLogEntry = {
    id: crypto.randomUUID(),
    date: new Date().toISOString(),
    serviceId: service.id,
    quantity: menge,
    performerId: 'system',
    performerName: kontext.performerName || 'System',
    isSubstitution: false,
    description: buildDescription(kontext, buchbar.length, menge, unit),
    bookingReference: unterweisungBookingReference(kontext.unterweisungNummer, kontext.datum),
  };

  return {
    services: newServices,
    logEntry,
    menge,
    teilnahmeIds: buchbar.map((t) => t.id),
    stundenposition,
  };
};
